import difference from 'lodash/difference'
import AdReportApi from '@/api/AdReport'
import { InsertionOrdersModel } from '@/models/InsertionOrders'
import { getters as currentAdvertiserGetter } from '@/vuex/modules/currentAdvertiser/constants'
import {
  fetchCampaignReport,
  fetchDeliveryGroupReport,
} from '@/models/StandardReport'
import { CampaignRepository } from '@/models/CampaignOperation'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { CampaignBudgetRepository } from '@/models/CampaignOperation/CampaignBudgetRepository'

dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

const getAdvId = rootGetters =>
  rootGetters[currentAdvertiserGetter.currentAdvertiser].id

/**
 *
 * @param {startTime,endTime} target
 * @param {startTime,endTime} range
 * return is target in range
 */
const checkInRange = (target, range) => {
  if (range.startTime === null && range.endTime === null) return true
  return (
    (dayjs(target.startTime).isSameOrAfter(dayjs(range.startTime)) ||
      dayjs(target.endTime).isSameOrAfter(dayjs(range.startTime))) &&
    (dayjs(target.startTime).isSameOrBefore(dayjs(range.endTime)) ||
      dayjs(target.endTime).isSameOrBefore(dayjs(range.endTime)))
  )
}

export default {
  UPDATE_PERIOD({ commit }, period) {
    commit('SET_PERIOD', period)
  },
  UPDATE_CALENDAR_RANGE({ commit }, range) {
    commit('SET_CALENDAR_RANGE', range)
  },
  SELECT_INSERTION_ORDERS({ commit }, insertionOrderIds) {
    commit('SET_SELECTED_IO_IDS', insertionOrderIds)
  },
  SELECT_BILLING_PERIODS(
    { commit, state, getters },
    { insertionOrderId, billingPeriodIds },
  ) {
    // 全案件のselectedBPと統合する
    const { selectedBillingPeriodIds } = state
    const { reportData } = getters
    const data = reportData.find(d => d.id === insertionOrderId)
    const brandnew = [
      ...difference(
        selectedBillingPeriodIds,
        data.billingPeriods.map(bp => bp.id),
      ),
      ...billingPeriodIds,
    ]
    commit('SET_BILLING_PERIOD_IDS', brandnew)
  },
  async FETCH_REPORT_DATA({ dispatch, commit }) {
    commit('SET_LOADING', true)
    // 案件の検索（最新のリストを取得）
    await dispatch('fetchInsertionOrders')
    // 案件idとbp id を集めてクエリを生成
    await Promise.all([
      dispatch('fetchTotalReport'),
      dispatch('fetchDailyReport'),
    ])
    commit('SET_LOADING', false)
  },
  async fetchInsertionOrders({ commit, getters, rootGetters }) {
    const advertiserId = getAdvId(rootGetters)
    const { range } = getters
    const params = {
      advertiserId,
      isIncludeDeleted: true,
      period: range.startDate === null ? undefined : range,
      withoutUnrequestedBudget: false,
    }
    let ret = await InsertionOrdersModel.search(params)
    commit(
      'SET_INSERTION_ORDERS',
      ret.items.map(i => new InsertionOrdersModel(i)),
    )
  },
  async fetchTotalReport({ commit, getters, rootGetters }) {
    const advertiserId = getAdvId(rootGetters)
    const { buildReportQuery } = getters
    const params = {
      query: buildReportQuery('total'),
    }
    const result = await AdReportApi.getInsertionOrderReport(
      advertiserId,
      params,
    )
    commit('SET_REPORT_DATA', result)
  },
  async fetchDailyReport({ commit, getters, rootGetters }) {
    const advertiserId = getAdvId(rootGetters)
    const { buildReportQuery } = getters
    const params = {
      query: buildReportQuery('daily'),
    }
    const result = await AdReportApi.getInsertionOrderReport(
      advertiserId,
      params,
    )
    commit('SET_DAILY_REPORT_DATA', result)
  },
  CLEAR_SELECTION({ commit }) {
    commit('SET_SELECTED_IO_IDS', null)
    commit('SET_BILLING_PERIOD_IDS', null)
  },
  RESET_SELECTION({ commit, state }) {
    const { reportData } = state
    // 取得した reportData がある案件のみ選択する
    commit(
      'SET_SELECTED_IO_IDS',
      reportData.data.map(datum => datum.insertionOrderId),
    )
    // 取得した reportData の案件が持つBPのみ選択する
    commit(
      'SET_BILLING_PERIOD_IDS',
      reportData.data.flatMap(datum => datum.insertionOrderBudgetIds),
    )
  },
  async FETCH_CAMPAIGN_REPORT(
    { commit, dispatch, getters, rootGetters },
    {
      insertionOrderId,
      insertionOrderBudgetIds,
      campaignBudgetIds,
      campaignId,
    },
  ) {
    const {
      range,
      genCampaignReportKey,
      reportData,
      getSelectedBillingPeriodIds,
    } = getters

    const campaignBudgets = [] // 期間の情報を持ったcampaignBudget
    if (campaignId) {
      const campBudgets = await CampaignBudgetRepository.getByCampaignId(
        campaignId,
      ).then(res => res.item.campaignBudgets)
      const selectedCampBudgets = campBudgets.filter(cBudget =>
        campaignBudgetIds.includes(cBudget.id),
      )
      campaignBudgets.push(...selectedCampBudgets)
    } else {
      const campaignIds = await CampaignRepository.search({
        advertiserId: getAdvId(rootGetters),
        insertionOrder: {
          ids: [insertionOrderId],
        },
      }).then(res => {
        return res.items.map(cBudget => cBudget.id)
      })
      const campBudgets = []
      await Promise.all(
        campaignIds.map(id =>
          CampaignBudgetRepository.getByCampaignId(id).then(res =>
            campBudgets.push(...res.item.campaignBudgets),
          ),
        ),
      )
      campaignBudgets.push(...campBudgets)
    }

    // キャンペーン予算を期間と案件予算期間でフィルター
    const filterdCampaignBudgetIds = campaignBudgets
      .filter(cBudget => {
        const insertionOrder = reportData.find(
          report => report.id === insertionOrderId,
        )
        if (insertionOrder === undefined) return false

        return insertionOrder.billingPeriods
          .filter(bp => getSelectedBillingPeriodIds.includes(bp.id))
          .some(bp => {
            return (
              // 案件予算期間のチェック
              checkInRange(cBudget, bp) &&
              // 期間のチェック
              checkInRange(cBudget, {
                startTime: range.startDate,
                endTime: range.endDate,
              })
            )
          })
      })
      .map(cBudget => cBudget.id)

    const result = await fetchCampaignReport({
      advertiserId: getAdvId(rootGetters),
      insertionOrderId: insertionOrderId,
      insertionOrderBudgetIds: insertionOrderBudgetIds,
      campaignBudgetIds: filterdCampaignBudgetIds,
      period: range,
    })

    commit('SET_CAMPAIGN_REPORT_DATA', {
      key: genCampaignReportKey(insertionOrderId),
      data: result,
      campaignId: campaignId,
    })

    await dispatch('fetchCampaigns', insertionOrderId)
  },
  // 配信グループレポートを取得
  async FETCH_DELIVERY_GROUP_REPORT(
    { commit, state, getters, rootGetters },
    { campaignId, campaignBudgetIds },
  ) {
    const {
      range,
      genDeliveryGroupReportKey,
      getSelectedBillingPeriodIds,
      reportData,
    } = getters
    const { campaignReportData } = state
    const ioList = Object.values(campaignReportData)
    const selectedBillingPeriodIds = []

    const rangedCampaignBudgetIds = ioList.reduce((ids, io) => {
      return [
        ...ids,
        ...io.data.reduce((accum, campaign) => {
          const insertionOrder = reportData.find(
            report => report.id === campaign.insertionOrderId,
          )
          if (insertionOrder === undefined) return [...accum]
          const selectedBillingPeriods = insertionOrder.billingPeriods.filter(
            bp => getSelectedBillingPeriodIds.includes(bp.id),
          )

          selectedBillingPeriodIds.push(
            ...selectedBillingPeriods.map(bp => bp.id),
          )

          return [
            ...accum,
            ...campaign.budgets
              .filter(budget => {
                return (
                  // 期間のチェック
                  checkInRange(budget, {
                    startTime: range.startDate,
                    endTime: range.endDate,
                  }) &&
                  // 案件予算期間のチェック
                  selectedBillingPeriods.some(bp => {
                    return checkInRange(budget, bp)
                  })
                )
              })
              .map(budget => budget.campaignBudgetId),
          ]
        }, []),
      ]
    }, [])

    const filterdCampaignBudgetIds =
      range.startDate || range.endDate
        ? campaignBudgetIds.filter(budget =>
            rangedCampaignBudgetIds.includes(budget),
          )
        : campaignBudgetIds

    const result = await fetchDeliveryGroupReport({
      advertiserId: getAdvId(rootGetters),
      campaignBudgetIds: filterdCampaignBudgetIds,
      campaignId: campaignId,
      ioBudgetIds: selectedBillingPeriodIds,
      period: range,
    })
    commit('SET_DELIVERY_GROUP_REPORT_DATA', {
      key: genDeliveryGroupReportKey(campaignId),
      data: result,
    })
  },
  // 選ばれているCampaignBudgetIdsを更新
  SET_SELECTED_CAMPAIGN_BUDGET_IDS(
    { commit },
    { campaignId, campaignBudgetIds },
  ) {
    commit('SET_SELECTED_CAMPAIGN_BUDGET_IDS', {
      campaignId,
      campaignBudgetIds,
    })
  },
  async fetchCampaigns({ commit, getters, rootGetters }, insertionOrderId) {
    const { genCampaignReportKey } = getters
    const result = await CampaignRepository.search({
      advertiserId: getAdvId(rootGetters),
      insertionOrder: {
        ids: [insertionOrderId],
      },
      includeDeleted: true,
    })
    commit('SET_CAMPAIGNS', {
      key: genCampaignReportKey(insertionOrderId),
      data: result.items,
    })
  },
}
